import {
    Box,
    Checkbox,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import type { FilaProductoCotizado } from "../types/cotizacionProveedores.types";
import { esSeleccionable, obtenerEstatusProducto, type SeleccionProductos } from "../utils/cotizacionSeleccion.utils";

interface Props {
    productos: FilaProductoCotizado[];
    esComputo: boolean;
    seleccion: SeleccionProductos;
    inventariosAutorizados: Set<number>;
    onToggle: (p: FilaProductoCotizado) => void;
    motivoBloqueoCotizacion: string | null;
    guardadosPorInventario: Record<number, string>;
}

const motivoBloqueo = (
    p: FilaProductoCotizado,
    autorizadoEnOtroProveedor: boolean,
    otroProveedor: boolean,
    esComputo: boolean
): string | null => {
    if (p.seleccionado === 3) return esComputo ? "Producto ya autorizado" : "Producto ya cotizado";
    if (autorizadoEnOtroProveedor)
        return esComputo
            ? "Ya autorizado con otro proveedor"
            : "Ya cotizado con otro proveedor";
    if (p.seleccionado === 2) return "Rechazado por DG";
    if (p.gestionado !== 1) return "El proveedor aún no gestiona este producto";
    if (p.disponibilidad !== 1) return "Proveedor sin disponibilidad";
    if (otroProveedor) return "Ya seleccionado con otro proveedor";
    return null;
};

const moneda = (n: number): string =>
    n.toLocaleString("es-MX", { style: "currency", currency: "MXN" });

const textoDisponibilidad = (d: number | null): string =>
    d === null ? "—" : d === 1 ? "Sí" : "No";

export const CotizacionProductosSubtabla = ({
    productos,
    esComputo,
    seleccion,
    inventariosAutorizados,
    onToggle,
    motivoBloqueoCotizacion,
    guardadosPorInventario
}: Props): JSX.Element => {
    const ESTATUS = obtenerEstatusProducto(esComputo);

    return (
        <Box sx={{ px: 0, py: 0, backgroundColor: "#f8fafc" }}>
            <Table size="small" sx={{ display: "table", width: "100%", tableLayout: "fixed" }}>
                <TableHead sx={{ display: "table-header-group", background: "#AAD6EC" }}>
                    <TableRow>
                        <TableCell sx={{ display: "table-cell", width: 48 }} />
                        <TableCell sx={{ display: "table-cell", width: "30%", fontSize: 12 }}>Producto</TableCell>
                        <TableCell sx={{ display: "table-cell", fontSize: 12 }} align="center">Cantidad</TableCell>
                        <TableCell sx={{ display: "table-cell", fontSize: 12 }} align="right">P. Unitario</TableCell>
                        <TableCell sx={{ display: "table-cell", fontSize: 12 }} align="right">Total</TableCell>
                        <TableCell sx={{ display: "table-cell", fontSize: 12 }} align="center">Disponible</TableCell>
                        <TableCell sx={{ display: "table-cell", width: 190, fontSize: 12 }} align="center">Estatus</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody sx={{ display: "table-row-group" }}>
                    {productos.map((p) => {
                        const idSel = seleccion[p.idInventario];
                        const guardado = p.seleccionado === 1;
                        //const enviado = inventariosEnviados.has(p.idInventario);
                        const idGuardado = guardadosPorInventario[p.idInventario];
                        const guardadoEnOtroProveedor = idGuardado !== undefined && idGuardado !== String(p.id);

                        // Guardado en BD con este proveedor y sin cambio local pendiente
                        const marcadoGuardado = guardado && idSel === undefined;
                        // Check visual: marcado local o guardado
                        const marcado = idSel === p.id || marcadoGuardado;

                        const otroProveedor = idSel !== undefined && idSel !== p.id;
                        const estatus = ESTATUS[p.seleccionado] ?? ESTATUS[0];
                        const autorizadoEsta = p.seleccionado === 3;
                        const autorizadoEnOtroProveedor = !autorizadoEsta && inventariosAutorizados.has(p.idInventario);
                        const autorizado = autorizadoEsta || autorizadoEnOtroProveedor;

                        const noDisponible = Number(p.gestionado) === 1 && Number(p.disponibilidad) === 0;
                        const seleccionadoLocal = idSel === p.id;
                        // Prioridad: autorizado > rechazado > sin disponibilidad > seleccionado local > guardado
                        const fondoFila =
                            p.seleccionado === 3
                                ? "#d5e3d8"
                                : p.seleccionado === 2
                                    ? "#fab4b4"
                                    : noDisponible
                                        ? "#ffe3e3"
                                        : seleccionadoLocal
                                            ? "#fff8d6"
                                            : marcadoGuardado
                                                ? "#e3f2fd"
                                                : "";

                        const fondoHover =
                            seleccionadoLocal
                                ? "#fff1b0"
                                : marcadoGuardado
                                    ? "#cfe8fb"
                                    : fondoFila || "#d3eaf5";

                        const habilitado =
                            !motivoBloqueoCotizacion &&
                            esSeleccionable(p) &&
                            !autorizado &&
                            !otroProveedor &&
                            !marcadoGuardado &&
                            !guardadoEnOtroProveedor;

                        const motivo = habilitado
                            ? null
                            : motivoBloqueoCotizacion
                                ? motivoBloqueoCotizacion
                                : marcadoGuardado
                                    ? "Seleccionado con este proveedor"
                                    : guardadoEnOtroProveedor
                                        ? "Ya seleccionado con otro proveedor"
                                        : motivoBloqueo(p, autorizadoEnOtroProveedor, otroProveedor, esComputo);

                        return (
                            <TableRow
                                key={p.id}
                                sx={{
                                    opacity: autorizadoEnOtroProveedor ? 0.55 : 1,
                                    background: fondoFila,
                                    "& > td:first-of-type": {
                                        boxShadow: noDisponible
                                            ? "inset 4px 0 0 #bd0606"
                                            : seleccionadoLocal
                                                ? "inset 4px 0 0 #f2b705"
                                                : marcadoGuardado
                                                    ? "inset 4px 0 0 #1d80c2"
                                                    : "none",
                                    },
                                    "&:hover": { background: fondoHover, fontWeight: 600 },
                                }}
                            >
                                <TableCell sx={{ py: 0, px: 0.5 }} align="center">
                                    <Tooltip title={motivo ?? ""} disableHoverListener={!motivo}>
                                        <span>
                                            <Checkbox
                                                size="small"
                                                checked={marcado}
                                                disabled={!habilitado}
                                                onChange={() => onToggle(p)}
                                            />
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ py: 0 }}>
                                    <Typography variant="caption" sx={{ fontSize: 12, display: "block" }}>
                                        {p.nombre}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="caption" sx={{ fontSize: 12, display: "block" }}>
                                        {p.cantidad}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="caption" sx={{ fontSize: 12, display: "block" }}>
                                        {moneda(p.precioUnitario)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="caption" sx={{ fontSize: 12, display: "block" }}>
                                        {moneda(p.total)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    {p.disponibilidad === null ? (
                                        <Typography variant="caption" sx={{ fontSize: 12, display: "block" }}>
                                            {textoDisponibilidad(p.disponibilidad)}
                                        </Typography>
                                    ) : (
                                        <Chip
                                            size="small"
                                            label={textoDisponibilidad(p.disponibilidad)}
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: 10,
                                                color: "#fff",
                                                backgroundColor: Number(p.disponibilidad) === 1 ? "#1dad76" : "#bd0606",
                                            }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <Chip
                                        size="small"
                                        label={estatus.label}
                                        sx={{ fontWeight: 600, fontSize: 10, backgroundColor: estatus.color, color: "#fff" }}
                                    />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
};